// ./dialogs/ExportOrderItemsDialog.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import API from '../../services/api';

// Utility function to format date as YYYY-MM-DD using local timezone (not UTC)
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Utility function to decode MIME encoded-word format
const decodeMimeEncodedWord = (str) => {
  // Check if it's a MIME encoded-word format
  const mimeMatch = str.match(/=\?(.+?)\?([bBqQ])\?(.+?)\?=/);
  
  if (!mimeMatch) {
    return str; // Return as-is if not MIME encoded
  }
  
  const [, charset, encoding, encodedText] = mimeMatch;
  
  if (encoding.toLowerCase() === 'b') {
    try {
      // Base64 decoding
      const decodedBytes = atob(encodedText);
      
      // Decode bytes as UTF-8
      const decoder = new TextDecoder(charset);
      const uint8Array = new Uint8Array(decodedBytes.length);
      for (let i = 0; i < decodedBytes.length; i++) {
        uint8Array[i] = decodedBytes.charCodeAt(i);
      }
      return decoder.decode(uint8Array);
    } catch (error) {
      console.error('Error decoding MIME word:', error);
      return str; // Return original on error
    }
  }
  
  // For quoted-printable or unsupported encoding, return as-is
  return str;
};

const ExportOrderItemsDialog = ({ 
  open, 
  onClose, 
  clientBusinessId, 
  clientName,
  orderLabel = 'הזמנה',
  orderPluralLabel = 'הזמנות',
  productLabel = 'מוצר',
  productPluralLabel = 'מוצרים'
}) => {
  // Start date is required, no default
  const [startDate, setStartDate] = useState(null);
  // End date defaults to today
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const errorRef = useRef(null);

  // Scroll to error when it appears
  useEffect(() => {
    if ((error || validationError) && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error, validationError]);

  // Reset dates when dialog opens
  useEffect(() => {
    if (open) {
      setStartDate(null);
      setEndDate(new Date());
      setError('');
      setValidationError('');
    }
  }, [open]);

  const validateDates = () => {
    if (!startDate) {
      setValidationError('יש לבחור תאריך התחלה');
      return false;
    }

    if (startDate && endDate && startDate > endDate) {
      setValidationError('תאריך ההתחלה לא יכול להיות אחרי תאריך הסיום');
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleExport = async () => {
    if (!validateDates()) {
      return;
    }
  
    setLoading(true);
    setError('');
  
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('business_id', clientBusinessId);
      
      // Format dates to YYYY-MM-DD using local timezone (not UTC to avoid day shift)
      params.append('start_date', formatDateLocal(startDate));
      params.append('end_date', formatDateLocal(endDate));
  
      // Make API call
      const response = await API.get(`/client/export-order-items/?${params.toString()}`, {
        responseType: 'blob' // Important for file download
      });
  
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from server's Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `order_items_${clientName}_${new Date().toISOString().split('T')[0]}.xlsx`; // fallback
      
      if (contentDisposition) {
        // First check if the entire header is MIME encoded
        if (contentDisposition.includes('=?') && contentDisposition.includes('?=')) {
          // Decode the entire header first
          const decodedHeader = decodeMimeEncodedWord(contentDisposition);
          
          // Now extract filename from the decoded header
          const filenameMatch = decodedHeader.match(/filename="?([^";\n]+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        } else {
          // Header is not MIME encoded, check for standard formats
          
          // First check for filename*= (RFC 5987) format
          const filenameStarMatch = contentDisposition.match(/filename\*=([^;]+)/);
          if (filenameStarMatch && filenameStarMatch[1]) {
            // Handle RFC 5987 encoded filename (e.g., UTF-8''encoded%20text)
            const encodedFilename = filenameStarMatch[1];
            const parts = encodedFilename.split("''");
            if (parts.length === 2) {
              filename = decodeURIComponent(parts[1]);
            }
          } else {
            // Check for regular filename= format
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          }
        }
      }
      
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
  
      // Close dialog after successful download
      onClose();
    } catch (err) {
      console.error('Export error:', err);
      if (err.response?.status === 404) {
        setError('הלקוח לא נמצא');
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.error || 'נתונים לא תקינים');
      } else {
        setError('שגיאה ביצוא הנתונים. אנא נסה שוב.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setStartDate(null);
      setEndDate(new Date());
      setError('');
      setValidationError('');
      onClose();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <MobileDialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 
          sx: { 
            fontSize: '1.2rem',
            borderRadius: 2,
            minHeight: '450px'
          } 
        }}
      >
        <DialogTitle 
          sx={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileDownloadIcon sx={{ fontSize: '1.8rem', color: 'primary.main' }} />
            יצוא סיכום {productPluralLabel}
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          {/* Client info */}
          <Box sx={{ 
            bgcolor: 'grey.50', 
            p: 2, 
            borderRadius: 1, 
            mb: 3,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              לקוח:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {clientName}
            </Typography>
          </Box>

          {error && (
            <Alert ref={errorRef} severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {validationError && (
            <Alert ref={!error ? errorRef : undefined} severity="warning" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Start Date - Required */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  color: 'text.secondary'
                }}
              >
                {/* <CalendarMonthIcon sx={{ fontSize: '1.2rem' }} /> */}
                תאריך התחלה
                <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
              </Typography>
              <DatePicker
                label="בחר תאריך התחלה"
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                  setValidationError('');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!validationError && !startDate,
                    sx: { 
                      fontSize: '1.2rem',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        direction: 'ltr'
                      }
                    }
                  }
                }}
              />
            </Box>

            {/* End Date - Has default value of today */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  color: 'text.secondary',
                }}
              >
                {/* <CalendarMonthIcon sx={{ fontSize: '1.2rem' }} /> */}
                תאריך סיום
              </Typography>
              <DatePicker
                label="תאריך סיום"
                value={endDate}
                onChange={(newValue) => {
                  setEndDate(newValue);
                  setValidationError('');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { 
                      fontSize: '1.2rem',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        direction: 'ltr'
                      }
                    }
                  }
                }}
              />
            </Box>

            {/* Info Alert */}
            <Alert 
              severity="info" 
              sx={{ 
                '& .MuiAlert-message': { 
                  fontSize: '1rem',
                  lineHeight: 1.5
                }
              }}
            >
              הקובץ יכלול:
              <ul style={{ margin: '8px 0 0 0', paddingRight: '20px' }}>
                <li>רשימת כל פריטי ה{orderPluralLabel} בטווח התאריכים שנבחר</li>
                <li>סיכום כמויות לפי {productLabel} ומחיר יחידה</li>
                <li>סה"כ כמויות וסכומים</li>
              </ul>
            </Alert>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            size="large"
            sx={{ 
              fontSize: '1.1rem',
              minWidth: '100px'
            }}
          >
            ביטול
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={loading || !startDate}
            startIcon={loading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            size="large"
            sx={{ 
              fontSize: '1.1rem',
              minWidth: '120px',
              backgroundColor: loading ? 'grey.400' : 'primary.main',
              direction: 'ltr',
            }}
          >
            {loading ? 'מייצא...' : 'יצוא'}
          </Button>
        </DialogActions>
      </MobileDialog>
    </LocalizationProvider>
  );
};

export default ExportOrderItemsDialog;